import React from "react";

interface PageProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Page wrapper component for consistent layout across all pages.
 * Provides standardized spacing, typography, and structure.
 */
export function Page({ title, subtitle, children, className }: PageProps) {
  return (
    <div className={`space-y-6 ${className || ""}`} data-testid="page-wrapper">
      <div className="space-y-2" data-testid="page-header">
        <h1 className="text-2xl font-semibold" data-testid="page-title">
          {title}
        </h1>
        {subtitle && (
          <p className="text-muted-foreground text-sm" data-testid="page-subtitle">
            {subtitle}
          </p>
        )}
      </div>

      <div className="space-y-6" data-testid="page-content">
        {children}
      </div>
    </div>
  );
}

/**
 * Section wrapper for grouping related content within a page.
 */
export function Section({ title, children, className }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`section space-y-4 ${className || ""}`} data-testid="section-wrapper">
      {title && (
        <h2 className="text-lg font-semibold" data-testid="section-title">
          {title}
        </h2>
      )}
      {children}
    </div>
  );
}

/**
 * Form group for consistent label + input styling.
 */
export function FormGroup({
  label,
  error,
  children,
  required,
  className,
}: {
  label?: string;
  error?: string;
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={`form-group ${className || ""}`} data-testid="form-group">
      {label && (
        <label className="block text-sm font-medium" data-testid="form-label">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="mt-1" data-testid="form-input">
        {children}
      </div>
      {error && (
        <small className="block mt-1 text-red-500" data-testid="form-error">
          {error}
        </small>
      )}
    </div>
  );
}

